import { useEffect, useState } from "react";
import { templatesApi } from "./api";

// Module-level cache so resolved signed URLs survive section switches and
// component remounts. Entries expire after 55 minutes (signed URLs typically
// last 1 hour) so a long session never uses a stale link.
const RESOLVED_CACHE = new Map<string, { url: string; expiresAt: number }>();
const PENDING_REQUESTS = new Map<string, Promise<string>>();
const TTL_MS = 55 * 60 * 1000;

function isAlreadySignedOrPublic(url: string): boolean {
  if (!url || !url.startsWith("http")) return true;
  if (url.includes("X-Amz-Signature") || url.includes("Signature=") || url.includes("AWSAccessKeyId=")) {
    return true;
  }
  return false;
}

export function useResolvedMediaUrl(url: string): string {
  const [resolved, setResolved] = useState<string>(() => {
    if (!url) return "";
    if (isAlreadySignedOrPublic(url)) return url;
    const cached = RESOLVED_CACHE.get(url);
    if (cached && cached.expiresAt > Date.now()) return cached.url;
    return "";
  });

  useEffect(() => {
    if (!url) {
      setResolved("");
      return;
    }
    if (isAlreadySignedOrPublic(url)) {
      setResolved(url);
      return;
    }
    const cached = RESOLVED_CACHE.get(url);
    if (cached && cached.expiresAt > Date.now()) {
      setResolved(cached.url);
      return;
    }

    let cancelled = false;
    let pending = PENDING_REQUESTS.get(url);
    if (!pending) {
      pending = templatesApi
        .resolveMediaUrl(url)
        .then((res) => {
          RESOLVED_CACHE.set(url, { url: res.url, expiresAt: Date.now() + TTL_MS });
          PENDING_REQUESTS.delete(url);
          return res.url;
        })
        .catch((e) => {
          PENDING_REQUESTS.delete(url);
          console.error("Failed to resolve header media url:", e);
          return url;
        });
      PENDING_REQUESTS.set(url, pending);
    }

    pending.then((resUrl) => {
      if (!cancelled) {
        setResolved(resUrl);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return resolved;
}
